import { useMemo } from "react";
import type { AristaGrafo, NodoGrafo, Punto } from "./definicion";
import { ANILLO, VIEWBOX, posicionDe } from "./definicion";
import { NodoCircuito } from "./NodoCircuito";

/**
 * El renderer del circuito: dibuja nodos y aristas a partir de los datos que
 * recibe (Tarea 5).
 *
 * No conoce ningún id ni nombre de agente. Los tramos "especiales" (el
 * fuera-de-camino) se ubican por su `tipo` — nunca por comparar `desde`/`hasta`
 * contra un id literal — así que mover ese tramo a otro par de nodos es editar
 * `definicion.ts`, no este archivo
 * (ADR-2026-08-16-estudio-marketing-mapa-grafo-declarativo).
 *
 * Dos aristas del dato NO se dibujan, y es a propósito
 * (ADR-2026-08-16-mapa-layout-manual-assets-marca):
 *  - las de `flujo` con los DOS extremos sobre el anillo, porque el trazo del
 *    anillo ya los une y el punteado en movimiento marca el sentido;
 *  - la de `retorno`, por lo mismo: cierra el ciclo entre dos nodos del
 *    anillo, que ya están conectados.
 * Siguen siendo ciertas y siguen en `definicion.ts`; lo que cambia es que el
 * anillo las dibuja mejor que una línea encima.
 *
 * Todos los trazos llevan un punteado que CORRE hacia donde apunta el trazo
 * (clase `.mapa-flujo`, keyframes en `index.css`). El sentido no se elige acá:
 * la animación empuja los guiones desde el punto inicial del trazo hacia el
 * final, así que alcanza con escribir cada trazo en el sentido del dato — que
 * es como ya venían (`desde` → `hasta`). El anillo es la excepción, y por eso
 * se dibuja como `<path>` de arcos explícitos: ver `caminoDelAnillo`.
 *
 * PERO SÓLO CORRE LO QUE ESTÁ CONECTADO (ver `fluye`): un trazo se anima
 * únicamente si TODOS los nodos que toca están `activo`. Si una punta está
 * `sin-conectar` o `no-existe`, el trazo se dibuja igual pero quieto — no es
 * un matiz visual, es que por ahí no está pasando nada. La regla sale del
 * campo `estado` del dato; acá no se conoce ningún id
 * (ADR-2026-08-17-mapa-anillo-punteado-y-flujo-por-estado).
 */

interface Props {
  nodos: NodoGrafo[];
  aristas: AristaGrafo[];
  onNavegar: (ruta: string) => void;
}

// ---- geometría del anillo -------------------------------------------------

function anguloDe(nodo: NodoGrafo): number {
  return nodo.posicion.tipo === "anillo" ? nodo.posicion.grados : 0;
}

function puntoEnAnillo(grados: number): Punto {
  const rad = (grados * Math.PI) / 180;
  return { x: ANILLO.cx + ANILLO.rx * Math.cos(rad), y: ANILLO.cy + ANILLO.ry * Math.sin(rad) };
}

/**
 * Un chevron por cada par de nodos consecutivos del anillo, en su punto medio
 * y apuntando en el sentido de recorrido (ángulo decreciente, que es el sentido
 * antihorario visual del circuito — ver definicion.ts).
 *
 * SE SIGUEN CALCULANDO SIEMPRE, PERO NO SIEMPRE SE VEN: con el punteado
 * corriendo, las puntas de flecha son una segunda señal de lo mismo y ensucian
 * el círculo, así que la clase `.mapa-chevron` los oculta. Aparecen sólo bajo
 * `prefers-reduced-motion`, donde la animación se detiene y sin ellos el
 * circuito se queda sin ninguna marca de sentido
 * (ADR-2026-08-17-mapa-orden-y-senales). La decisión es del CSS
 * —es una consulta de medio— y por eso no hay `matchMedia` acá.
 */
function chevronesDelAnillo(nodosAnillo: NodoGrafo[]) {
  if (nodosAnillo.length < 2) return [];
  const orden = [...nodosAnillo].sort((a, b) => anguloDe(b) - anguloDe(a));
  return orden.map((nodo, i) => {
    const siguiente = orden[(i + 1) % orden.length];
    const a1 = anguloDe(nodo);
    let a2 = anguloDe(siguiente);
    if (a2 > a1) a2 -= 360; // cruza el 0°: seguir bajando el ángulo
    const medio = (a1 + a2) / 2;
    const rad = (medio * Math.PI) / 180;
    const dx = ANILLO.rx * Math.sin(rad);
    const dy = -ANILLO.ry * Math.cos(rad);
    return { ...puntoEnAnillo(medio), angulo: (Math.atan2(dy, dx) * 180) / Math.PI };
  });
}

/**
 * El anillo, como PATH EXPLÍCITO y escrito en el sentido del circuito. Es la
 * única forma de animarlo: un `<ellipse>` de SVG se recorre en el sentido que
 * decide el motor (θ creciente desde el eje +x), que con la `y` hacia abajo es
 * el HORARIO visual — al revés del ciclo.
 *
 * Acá el recorrido es explícito: arranca en θ=0° (costado derecho) y BAJA el
 * ángulo, que es el sentido antihorario visual en el que están ordenados los
 * nodos y apuntan los chevrones (ver definicion.ts). Lo dice el `sweep-flag`
 * en 0: por definición del arco elíptico de SVG, `sweep-flag = 1` recorre con
 * θ creciente y `0` con θ decreciente.
 *
 * Son DOS medias vueltas porque un arco de 360° no existe en SVG: con los dos
 * extremos en el mismo punto el arco degenera y no se dibuja nada.
 */
function caminoDelAnillo(): string {
  const derecha = puntoEnAnillo(0); // θ = 0°   → (cx + rx, cy)
  const izquierda = puntoEnAnillo(180); // θ = 180° → (cx - rx, cy)
  const arco = `A ${ANILLO.rx} ${ANILLO.ry} 0 0 0`;
  const p = (punto: Punto) => `${punto.x.toFixed(2)} ${punto.y.toFixed(2)}`;
  return `M ${p(derecha)} ${arco} ${p(izquierda)} ${arco} ${p(derecha)}`;
}

const CAMINO_ANILLO = caminoDelAnillo();

/**
 * `pathLength` del anillo animado: le declara al motor que el trazo "mide"
 * 1200, y los guiones pasan a contarse en esa escala. Sirve para que el
 * patrón cierre EXACTO en la costura (1200 / 12 = 100 repeticiones justas de
 * `TRAMA_ANILLO`); con el largo real de la elipse (~1131) sobraba un pedazo de
 * guión en el punto de cierre.
 */
const LARGO_ANILLO = 1200;

/**
 * EL PESO DEL ANILLO. Antes el anillo se dibujaba DOS veces: una `<ellipse>`
 * maciza gris y el punteado animado corriendo encima. Se sacó la maciza —
 * el punteado sobre una línea sólida se leía como un adorno pegado a otra
 * cosa, no como el circuito moviéndose.
 *
 * Pero el anillo es la FORMA DOMINANTE de la pantalla, y el punteado que había
 * (trama "2 10" a grosor 2 y alfa 0.30) pesaba menos que cualquier lectura de
 * memoria: 2 de tinta cada 12 son 17% de cobertura, o sea una ronda de puntitos
 * sueltos. Sin la elipse abajo, el círculo directamente dejaba de leerse.
 * Se compensa en los tres ejes a la vez, que es lo que hace falta para que un
 * punteado pese como una forma y no como una guía:
 *
 *  - TRAMA "3 9" en vez de "2 10": guión 50% más largo. Sigue con período 12,
 *    que es lo que sostiene las dos costuras — cierra en 100 repeticiones
 *    justas de los 1200 declarados y divide los 24 del keyframe, así que el
 *    ciclo de la animación tampoco salta (ver `.mapa-flujo` en index.css).
 *  - GROSOR 3 en vez de 2. Con `stroke-linecap="round"` el grosor no sólo
 *    engorda el trazo: cada guión gana medio grosor de casquete por punta, así
 *    que el guión que en escala del path mide 3 (≈2.8 unidades reales, por el
 *    1131/1200) se dibuja de ~5.8 y el hueco baja a ~5.5. Guión y hueco casi
 *    iguales: se lee como línea continua de lejos y como punteado corriendo de
 *    cerca, que es exactamente lo que se busca.
 *  - ALFA 0.45 en vez de 0.30, para que el azul aguante el grosor sin quedar
 *    lavado contra el fondo de puntos.
 *
 * Queda como el trazo más pesado del mapa —más que los flujos de las fuentes
 * (1.6 a 0.40) y que las lecturas (1 a 4-4)—, que es el orden correcto: el
 * anillo ES el circuito y todo lo demás cuelga de él.
 */
const TRAMA_ANILLO = "3 9";
const GROSOR_ANILLO = 3;
const ALFA_ANILLO = 0.45;

/** Interpola dos ángulos por el camino CORTO: sin esto, un tramo que cruza el
 *  0° (350° → 10°) daría la vuelta larga por todo el anillo. */
function anguloInterpolado(desde: number, hasta: number, fraccion: number): number {
  let delta = hasta - desde;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return desde + delta * fraccion;
}

/**
 * El trazo de una arista: SIEMPRE una recta de punta a punta.
 *
 * Acá vivía el arqueo de los pares ida-y-vuelta: cuando el dato declaraba dos
 * aristas entre los mismos dos nodos, las dos líneas caían sobre el mismo eje y
 * la de encima tapaba entera a la de abajo, así que cada una se desviaba hacia
 * su izquierda con una cuadrática. El único par así era
 * `personas ⇄ estratega`, y la vuelta se borró: ese vínculo es una sola línea
 * recta (ADR-2026-08-17-mapa-orden-y-senales). Sin pares repetidos el
 * desvío no tenía a quién aplicarse, y una rama muerta que nadie ejecuta es una
 * rama que se rompe sin que nadie lo note. Si mañana vuelve a haber ida y
 * vuelta entre dos nodos, esto se reescribe con el caso a la vista.
 */
function caminoArista([p1, p2]: [Punto, Punto]): string {
  return `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`;
}

export const CircuitoMapa = ({ nodos, aristas, onNavegar }: Props) => {
  const porId = useMemo(() => new Map(nodos.map((n) => [n.id, n])), [nodos]);

  const chevrones = useMemo(
    () => chevronesDelAnillo(nodos.filter((n) => n.posicion.tipo === "anillo")),
    [nodos],
  );

  const estaEnAnillo = (id: string) => porId.get(id)?.posicion.tipo === "anillo";

  /** Los dos extremos de una arista, o `null` si le falta un nodo. El de
   *  llegada cae sobre el anillo cuando la arista declara un `tramo`. */
  const puntosDe = (a: AristaGrafo | undefined): [Punto, Punto] | null => {
    if (!a) return null;
    const n1 = porId.get(a.desde);
    const n2 = porId.get(a.hasta);
    if (!n1 || !n2) return null;
    if (!a.tramo) return [posicionDe(n1), posicionDe(n2)];
    const n3 = porId.get(a.tramo.hacia);
    if (!n3 || n2.posicion.tipo !== "anillo" || n3.posicion.tipo !== "anillo") return null;
    const grados = anguloInterpolado(n2.posicion.grados, n3.posicion.grados, a.tramo.en);
    return [posicionDe(n1), puntoEnAnillo(grados)];
  };

  /** Una arista se dibuja salvo que el anillo ya la trace: las de `flujo` con
   *  los dos extremos sobre el anillo y la de `retorno` (ver cabecera). */
  const seDibuja = (a: AristaGrafo) =>
    a.tipo !== "retorno" && !(a.tipo === "flujo" && estaEnAnillo(a.desde) && estaEnAnillo(a.hasta));

  /**
   * Si por esa arista ESTÁ PASANDO ALGO: todos los nodos que toca están
   * `activo`. Con una punta `sin-conectar` o `no-existe` el trazo se dibuja
   * igual —el vínculo es cierto, es parte del diseño del circuito— pero
   * quieto: un punteado corriendo hacia una memoria que todavía no existe
   * afirma un movimiento que no ocurre.
   *
   * Cuenta también `tramo.hacia` cuando la arista termina sobre un arco del
   * anillo: el punto de llegada es una INTERPOLACIÓN entre `hasta` y `hacia`,
   * así que los dos son extremos reales de ese trazo.
   *
   * Sale del campo `estado` del dato, nunca de un id: apagar un agente en
   * `definicion.ts` apaga sus trazos sin tocar este archivo.
   */
  const fluye = (a: AristaGrafo | undefined): boolean => {
    if (!a) return false;
    const tocados = [a.desde, a.hasta, ...(a.tramo ? [a.tramo.hacia] : [])];
    return tocados.every((id) => porId.get(id)?.estado === "activo");
  };

  /** La clase de animación, o nada si por ese trazo no fluye. */
  const claseFlujo = (a: AristaGrafo | undefined) => (fluye(a) ? "mapa-flujo" : undefined);

  /** El anillo corre sólo si el CICLO ENTERO está en pie: alcanza con una
   *  etapa caída para que no haya vuelta completa que dar. Mismo criterio que
   *  `fluye`, aplicado a los nodos del anillo en vez de a los de una arista. */
  const anilloFluye = useMemo(
    () =>
      nodos
        .filter((n) => n.posicion.tipo === "anillo")
        .every((n) => n.estado === "activo"),
    [nodos],
  );

  /** El `d` de una arista. */
  const caminoDe = (a: AristaGrafo | undefined): string | null => {
    const p = puntosDe(a);
    return p && caminoArista(p);
  };

  // Las de `flujo` con los dos extremos sobre el anillo no se dibujan: el
  // anillo ya las traza. Las que entran desde afuera (las fuentes) sí.
  // Cada trazo se lleva su clase de animación resuelta (ver `fluye`).
  const flujos = useMemo(
    () =>
      aristas
        .filter((a) => a.tipo === "flujo" && seDibuja(a))
        .map((a) => {
          const puntos = puntosDe(a);
          return puntos && { puntos, clase: claseFlujo(a) };
        })
        .filter((f): f is { puntos: [Punto, Punto]; clase: string | undefined } => f !== null),
    [aristas, porId],
  );

  // Las lecturas van directas de cada nodo a su destino: un trazo por arista,
  // sin punto de reparto intermedio.
  const lecturas = useMemo(
    () =>
      aristas
        .filter((a) => a.tipo === "lectura")
        .map((a) => {
          const d = caminoDe(a);
          return d && { d, clase: claseFlujo(a) };
        })
        .filter((l): l is { d: string; clase: string | undefined } => l !== null),
    [aristas, porId],
  );

  const fueraDeCamino = aristas.find((a) => a.tipo === "fuera-de-camino");

  const puntosFuera = puntosDe(fueraDeCamino);

  // `w-full h-full`: el contenedor ya viene con la relación de aspecto EXACTA
  // del viewBox (MapaPage), así que llenarlo entero no deforma nada y garantiza
  // que no quede franja vacía adentro — de esa coincidencia depende el bloque
  // que MapaPage ancla en el centro del anillo.
  return (
    <svg viewBox={VIEWBOX} className="block h-full w-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="fondo-puntos" width="16" height="16" patternUnits="userSpaceOnUse">
          <circle cx="8" cy="8" r="1" fill="#02062B" opacity="0.07" />
        </pattern>
        {/* Glifos: uno por `GlifoNodo`. NodoCircuito los referencia con
            <use href="#glifo-..."> según el campo `glifo` del nodo — nunca
            según su id. */}
        <symbol id="glifo-datos" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
          <path d="M -7 6 V -1 M 0 6 V -7 M 7 6 V 2" />
        </symbol>
        <symbol id="glifo-estratega" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <circle r="7" />
          <circle r="2.4" />
        </symbol>
        <symbol id="glifo-creativo" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
          <path d="M 0 -8 V 8 M -8 0 H 8 M -5.6 -5.6 L 5.6 5.6 M -5.6 5.6 L 5.6 -5.6" />
        </symbol>
        <symbol id="glifo-ia" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M -6 5 L 0 -6 L 6 5 Z" />
          <circle cx="-6" cy="5" r="1.3" fill="currentColor" stroke="none" />
          <circle cx="6" cy="5" r="1.3" fill="currentColor" stroke="none" />
          <circle cx="0" cy="-6" r="1.3" fill="currentColor" stroke="none" />
        </symbol>
        <symbol id="glifo-calidad" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M -6 0 L -1.5 5 L 6 -6" />
        </symbol>
        <symbol id="glifo-armado" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
          <path d="M -7 -4 L 0 -1 L 7 -4 M -7 0 L 0 3 L 7 0 M -7 4 L 0 7 L 7 4" />
        </symbol>
        <symbol id="glifo-humano" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
          <circle cx="0" cy="-3.5" r="3" />
          <path d="M -6 8 C -6 2 6 2 6 8" />
        </symbol>
        <symbol id="glifo-copy" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round">
          <path d="M -7 -5 H 7 M -7 0 H 7 M -7 5 H 3" />
        </symbol>
        <symbol id="glifo-memoria" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round">
          <ellipse cx="0" cy="-5" rx="6" ry="2.2" />
          <path d="M -6 -5 V 5 C -6 6.2 6 6.2 6 5 V -5" />
        </symbol>
        {/* Publicar = sale de la bandeja hacia afuera. Ninguno de los otros
            ocho dice eso, y el que usaría si no —el de `humano`— es justo lo
            que el nodo deja de ser cuando se publica solo. */}
        <symbol id="glifo-publicacion" overflow="visible" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
          <path d="M -7 2 V 7 H 7 V 2" />
          <path d="M 0 4 V -7 M -4.2 -2.8 L 0 -7 L 4.2 -2.8" />
        </symbol>
      </defs>

      {/* 1. fondo de matriz de puntos */}
      <rect x="0" y="0" width="100%" height="100%" fill="url(#fondo-puntos)" />

      {/* 2. el anillo: UN solo trazo punteado, escrito en el sentido del ciclo
             (ver `caminoDelAnillo`) y dimensionado para sostener la pantalla
             sin la elipse maciza que había debajo (ver `TRAMA_ANILLO`). Corre
             sólo si el ciclo entero está en pie. */}
      <path d={CAMINO_ANILLO} pathLength={LARGO_ANILLO} fill="none"
            stroke="#1644EB" strokeOpacity={ALFA_ANILLO} strokeWidth={GROSOR_ANILLO}
            strokeLinecap="round" strokeDasharray={TRAMA_ANILLO}
            className={anilloFluye ? "mapa-flujo" : undefined} />

      {/* 3. chevrones de dirección: ocultos mientras el punteado corra, visibles
             bajo `prefers-reduced-motion` (ver `chevronesDelAnillo` y
             `.mapa-chevron` en index.css) */}
      {chevrones.map((c, i) => (
        <path key={i} className="mapa-chevron" d="M -4 -3 L 4 0 L -4 3 Z" fill="#AEB5C6"
              transform={`translate(${c.x} ${c.y}) rotate(${c.angulo})`} />
      ))}

      {/* 4. flujo que entra al circuito desde afuera (las fuentes).
             Cada uno es la línea sólida de siempre + un punteado tenue que
             corre encima: el trazo va de `desde` a `hasta`, y la animación
             (offset a negativo) empuja los puntos hacia el final, o sea hacia
             el nodo al que entra el dato. La fuente que todavía no está
             conectada dibuja las dos líneas igual, pero quietas (ver `fluye`). */}
      {flujos.map(({ puntos: [p1, p2], clase }, i) => (
        <g key={i}>
          <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#C2C8D6" strokeWidth={1.2} />
          <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke="#1644EB" strokeOpacity={0.4} strokeWidth={1.6}
                strokeLinecap="round" strokeDasharray="2 10" className={clase} />
        </g>
      ))}

      {/* 5. lecturas de memoria, un trazo RECTO y directo por arista. Ya venían
             punteadas, así que fluyen ellas mismas: el trazo arranca en la
             memoria y termina en quien la lee, y los guiones corren en ese
             sentido. */}
      {lecturas.map(({ d, clase }, i) => (
        <path key={i} d={d} fill="none"
              stroke="#B9B5E8" strokeWidth={1} strokeDasharray="4 4" className={clase} />
      ))}

      {/* 6. la arista fuera-de-camino */}
      {puntosFuera && (
        <line x1={puntosFuera[0].x} y1={puntosFuera[0].y} x2={puntosFuera[1].x} y2={puntosFuera[1].y}
              stroke="#C2C8D6" strokeWidth={1} strokeDasharray="3 3"
              className={claseFlujo(fueraDeCamino)} />
      )}

      {/* 7. los nodos */}
      {nodos.map((nodo) => (
        <NodoCircuito key={nodo.id} nodo={nodo} onNavegar={onNavegar} />
      ))}
    </svg>
  );
};

export default CircuitoMapa;
